/* maaho-rules.js - which option combinations the workshop can actually make.

   Eleven rules that disable, hide, force or clear a choice as the customer
   configures: eleven shapes that cannot be sewn with a classic seam, foam that
   cannot go under a furniture seam, thread colour that disappears on leather,
   a size cap on thick foam for deliveries to Norway.

   They are data, not code - shop.metafields.maaho.attribute_rules, written by
   shopify-migration/load-attribute-rules.py and emitted into the page by
   sections/maaho-product.liquid as window.maahoAttributeRules, carrying only the
   rules that touch this product's groups and only the customer's own language.

   The same rules are checked again at checkout by the attribute-rules validation
   function, because a basket can be filled without this file ever running. Both
   read the same metafield; neither holds a rule of its own.

   Two kinds of action, and the difference is the whole design:

     constraints  disable, enable, hide, show, restrict - re-applied after every
                  change, so the outcome never depends on which control was
                  touched last.
     transitions  select, clear - fired only when a rule crosses between its two
                  branches, the way the old shop fires them on a click. A
                  constraint would fight the customer: rule 1 chooses the classic
                  seam for an ordinary shape, and re-choosing it on every pass
                  would make a French seam impossible to pick.

   Whatever the rules do, a chosen value that has just become unavailable is
   always cleared, so no configuration survives a rule that forbids it.

   Reads window.MaahoProductGroups, registered by assets/maaho-product.js.

   evaluate() touches no DOM, so the checkout validation function bundles this
   very file and runs the same rules on the finished basket -- see
   app/maaho-pricing/extensions/attribute-rules. Keep it that way: anything added
   below that needs a document belongs in start(), not in evaluate(). */

/* globalThis, not window: this same file is bundled into the checkout validation
   function, which has no DOM. In a browser the two are the same object, so the
   page still reads window.MaahoRules. */
globalThis.MaahoRules = (function () {
  'use strict';

  /* A forced choice can satisfy the next rule's condition, so evaluation repeats
     until the selection settles. The longest chain in the rules is shape → seam →
     foam, so this is well clear. */
  var MAX_PASSES = 4;

  var COMBINED_SIZE_FORMULA = 'width + 2 * second';

  /* --- The evaluator: no DOM, so the checkout function can run the same rules --- */

  function conditionMet(condition, chosen) {
    if (!condition) return true;
    return (condition.anySelected || []).some(function (id) {
      return chosen.indexOf(Number(id)) > -1;
    });
  }

  function ruleApplies(rule, context) {
    /* Switched off in the admin. Checked here rather than filtered out of the page,
       so the product page and the checkout function honour it from the one place
       they both run -- a rule off in one and on in the other is the worst outcome
       there is. */
    if (rule.enabled === false) return false;

    /* `touches` is every group the rule reaches, derived by
       load-attribute-rules.py. A product offering none of them is not a product
       this rule has anything to say about -- without this, the seam rules would
       reach for a seam on a tablecloth. */
    var touchesThisProduct = !rule.touches || !rule.touches.length
      || rule.touches.some(function (groupId) { return context.hasGroup(groupId); });

    if (!touchesThisProduct) return false;
    if (rule.requiresGroup && !context.hasGroup(rule.requiresGroup)) return false;
    if (rule.countries && rule.countries.indexOf(context.country) === -1) return false;
    if (rule.products && rule.products.indexOf(context.handle) === -1) return false;
    return true;
  }

  function runAction(action, decision, chosen, context) {
    /* `unless` is the exception a rule carries: 3 cm foam comes back when the seam
       allows it, but not while a half-round cushion is chosen; the classic seam
       comes back for an ordinary shape, but not for the eleven that forbid it. It
       guards the whole action, so one clause can hold back everything it does. */
    if (action.unless && conditionMet(action.unless, chosen)) return;

    if (action.disable) {
      action.disable.forEach(function (id) { decision.unavailable[id] = true; });
    }

    if (action.enable) {
      action.enable.forEach(function (id) { decision.unavailable[id] = false; });
    }

    if (action.restrictGroupTo) {
      decision.allowedValues[action.restrictGroupTo.group] =
        action.restrictGroupTo.values ? action.restrictGroupTo.values.map(Number) : null;
    }

    if (action.hideGroup !== undefined) decision.hiddenGroups[action.hideGroup] = true;
    if (action.showGroup !== undefined) decision.hiddenGroups[action.showGroup] = false;

    if (action.select !== undefined) decision.transitions.push({ select: Number(action.select) });
    if (action.clearGroup !== undefined) decision.transitions.push({ clear: String(action.clearGroup) });

    if (action.limitCombinedSize) {
      var limit = action.limitCombinedSize;
      var size = context.measurements();
      if (size && limit.formula === COMBINED_SIZE_FORMULA
          && size.width + 2 * size.second > limit.maximum) {
        decision.blocked.push({ message: limit.message, highlight: 'width' });
      }
    }

    if (action.requireSecondLessThanWidth) {
      var measured = context.measurements();
      if (measured && measured.second >= measured.width) {
        decision.blocked.push({ highlight: action.requireSecondLessThanWidth.highlight });
      }
    }
  }

  /**
   * @param {Array}  rules    the rules that touch this product
   * @param {Array}  chosen   every attribute id currently selected, as numbers
   * @param {object} context  {country, handle, hasGroup(), measurements()}
   * @returns {object} what the page should look like, and what it may not sell
   */
  function evaluate(rules, chosen, context) {
    var decision = {
      unavailable: {},     // attribute id -> true when it may not be chosen
      hiddenGroups: {},    // group id -> true when the whole group disappears
      allowedValues: {},   // group id -> the only values it may show, or null for all
      transitions: [],     // {select} / {clear}, in the order the rules declare them
      branches: {},        // rule id -> which branch it took, to spot a crossing
      blocked: []          // {message, highlight} - reasons this cannot be ordered
    };

    rules.forEach(function (rule) {
      if (!ruleApplies(rule, context)) return;

      var met = conditionMet(rule.when, chosen);
      decision.branches[rule.id] = met;

      (met ? rule.then : rule.otherwise || []).forEach(function (action) {
        runAction(action, decision, chosen, context);
      });
    });

    return decision;
  }

  /* --- Applying it to the page --- */

  function start() {
    var data = window.maahoAttributeRules;
    var groups = window.MaahoProductGroups;
    if (!data || !data.rules || !data.rules.length || !groups) return;

    var warning = document.getElementById('rule-warning');
    var branches = {};          // what each rule decided last time round
    var settled = false;        // true once the first evaluation has run
    var blocked = [];

    var context = {
      country: data.country,
      handle: data.handle,
      hasGroup: function (groupId) { return Boolean(groups.byGroupId[groupId]); },
      measurements: measurements
    };

    /* Both boxes filled and sane, or nothing: a half-typed measurement is not a
       rule violation, it is a customer mid-sentence. The second repeats the width
       when the shape has only one axis, exactly as the price does. */
    function measurements() {
      var width = numberIn('measurement-width');
      if (!width) return null;
      var second = numberIn('measurement-length');
      if (!second) {
        var single = window.maahoMeasurements && window.maahoMeasurements.secondFromWidth;
        if (!single) return null;
        second = width;
      }
      return { width: width, second: second };
    }

    function numberIn(id) {
      var input = document.getElementById(id);
      if (!input || input.disabled) return 0;
      var value = parseFloat(input.value);
      return isNaN(value) ? 0 : value;
    }

    function chosenIds() {
      return groups.all().map(function (group) { return Number(group.chosenId()); })
                         .filter(Boolean);
    }

    /** Paint the constraints. Returns true when a group had to lose its choice. */
    function paint(decision) {
      var repaired = false;

      groups.all().forEach(function (group) {
        var allowed = decision.allowedValues[group.groupId];
        var groupHidden = decision.hiddenGroups[group.groupId] === true;
        if (group.setHidden(groupHidden)) repaired = true;

        Object.keys(group.values).forEach(function (attributeId) {
          var hidden = Boolean(allowed) && allowed.indexOf(Number(attributeId)) === -1;
          var unavailable = decision.unavailable[attributeId] === true;
          group.setValueState(attributeId, { hidden: hidden, unavailable: unavailable });

          if ((hidden || unavailable) && String(group.chosenId()) === attributeId) {
            group.clear();
            repaired = true;
          }
        });
      });

      return repaired;
    }

    /** Fire select/clear for the rules that have just crossed between branches. */
    function crossings(decision) {
      var changed = false;

      decision.transitions.forEach(function (transition) {
        if (transition.clear !== undefined) {
          var group = groups.byGroupId[transition.clear];
          if (group && group.chosenId()) { group.clear(); changed = true; }
          return;
        }
        var owner = groups.holding(transition.select);
        if (owner && owner.canSelect(transition.select)
            && String(owner.chosenId()) !== String(transition.select)) {
          owner.select(transition.select);
          changed = true;
        }
      });

      return changed;
    }

    /** Only the rules whose branch moved since last time may force a choice. */
    function movedOnly(decision) {
      var moved = { transitions: [] };
      var rules = data.rules;

      rules.forEach(function (rule) {
        var now = decision.branches[rule.id];
        if (now === undefined || branches[rule.id] === now) return;

        var actions = now ? rule.then : rule.otherwise || [];
        actions.forEach(function (action) {
          if (action.select !== undefined) moved.transitions.push({ select: Number(action.select) });
          if (action.clearGroup !== undefined) moved.transitions.push({ clear: String(action.clearGroup) });
        });
      });

      return moved;
    }

    function showWarning() {
      if (!warning) return;
      var message = blocked.map(function (reason) {
        return reason.message ? data.messages[reason.message] : '';
      }).filter(Boolean)[0];

      warning.textContent = message || '';
      warning.hidden = !message;

      /* No message for the length/width rule - the old shop shows none either.
         The box it is about turns red instead, which is what the page already
         does for a measurement out of range. */
      ['measurement-width', 'measurement-length'].forEach(function (id) {
        var input = document.getElementById(id);
        if (input) input.classList.remove('invalid-by-rule');
      });
      blocked.forEach(function (reason) {
        var id = reason.highlight === 'second' ? 'measurement-length' : 'measurement-width';
        var input = document.getElementById(id);
        if (input && !input.disabled) input.classList.add('invalid-by-rule');
      });
    }

    function run() {
      for (var pass = 0; pass < MAX_PASSES; pass++) {
        var decision = evaluate(data.rules, chosenIds(), context);
        var repaired = paint(decision);
        var forced = settled ? crossings(movedOnly(decision)) : false;

        branches = decision.branches;
        settled = true;
        blocked = decision.blocked;

        if (!repaired && !forced) break;
      }

      showWarning();
      groups.refresh();
    }

    groups.addReadinessCheck(function () { return blocked.length === 0; });
    groups.onChange(run);
    run();
  }

  return { evaluate: evaluate, start: start };
})();

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', globalThis.MaahoRules.start);
  } else {
    globalThis.MaahoRules.start();
  }
}
